import * as React from 'react'
import styled from 'styled-components'

import * as hooks from '@owl-nest/hooks'
import * as logger from '@owl-nest/logger'
import { useCart, useShippingRates, ShippingCountries, useShippingCountry } from '@boutique/cart'
import * as entity from '@boutique/entities'
import * as money from '@owl-nest/money'
import * as utils from '@boutique/utils'
import * as url from '@boutique/url'

import * as plume from '@ulule/owl-kit-components/next'

import { type SelectedVariant } from '../hooks'

export type ShippingProps = {
  product: entity.product.Product
  selectedVariant: SelectedVariant
}

export function Shipping({ product, selectedVariant }: ShippingProps): React.ReactElement<ShippingProps> {
  const cart = useCart()

  const [shippingCountry, setSelectedCountry] = useShippingCountry()

  const variant = {
    shopifyVariantId: selectedVariant?.numericId ?? product.variants[0].numericId,
    quantity: 1,
  }

  const response = useShippingRates({
    items: [variant],
    vendors: [product.vendor.identifier],
    fetchOnMount: true,
  })
  const freeShippingThresholds = response.data?.freeShippingThresholds
  const supportedCountries = response.data?.supportedCountries
  const rates = response.data?.rates

  React.useEffect(() => {
    if (response.status === hooks.QueryStatus.FAILURE) {
      logger.err(`[product] Couldn't retrieve shipping rates for shopifyVariant:${variant.shopifyVariantId}`, {
        country: shippingCountry,
        error: response.failure,
        product,
      })
    }
  }, [response.status])

  if (response.status === hooks.QueryStatus.FAILURE || product.vendor === undefined) {
    return (
      <plume.Toast closed={false} type="error">
        Une erreur est survenue pendant l'estimation des frais de livraison. Aucune crainte : nous allons corriger ça au
        plus vite !
      </plume.Toast>
    )
  }

  const brandFreeShippingThreshold =
    freeShippingThresholds && freeShippingThresholds[product.vendor.identifier]
      ? freeShippingThresholds[product.vendor.identifier]
      : null

  const total = cart.ready ? cart.total(product.vendor.identifier) : 0

  return (
    <>
      <BrandText>Vendu et expédié par {product.vendor.name}</BrandText>
      <Container>
        {product.isGiftCard ? (
          <plume.styles.copy.S>
            Vous recevez la carte cadeau dématérialisée par email immédiatement après la commande. Elle contient un code
            utilisable sans limite de durée.
          </plume.styles.copy.S>
        ) : (
          <ShippingDetails>
            <plume.styles.copy.S>
              Indiquez-nous votre pays pour connaître les frais de livraison chez vous.
            </plume.styles.copy.S>

            <ShippingCountries
              selectedCountry={shippingCountry}
              setSelectedCountry={setSelectedCountry}
              supportedCountries={supportedCountries}
            />

            {rates && rates.length === 0 && <p>Aucune estimation disponible pour ce pays.</p>}
            {rates && rates.length > 0 && (
              <ShippingList>
                {rates.map((rate) => (
                  <ShippingRate key={rate.name}>
                    <div>
                      <plume.styles.copy.M>{rate.friendlyName}</plume.styles.copy.M>
                      <plume.styles.copy.S>
                        {utils.shipping.getEstimatedTimeOfArrivalLabel(rate.name)}
                      </plume.styles.copy.S>
                    </div>
                    <plume.styles.copy.L>{rate.price === 0 ? 'Offerte' : money.format(rate.price)}</plume.styles.copy.L>
                  </ShippingRate>
                ))}
              </ShippingList>
            )}
          </ShippingDetails>
        )}
        <Sidebar>
          {brandFreeShippingThreshold !== null && (
            <SidebarItem>
              <plume.styles.copy.M>
                La livraison standard est gratuite à partir de {money.format(brandFreeShippingThreshold)} d’achats
              </plume.styles.copy.M>
              <plume.styles.copy.S>
                {utils.shipping.getFreeShippingThresholdMessage({
                  brandName: product.vendor.name,
                  remainingAmountBeforeFreeShipping: utils.shipping.getRemainingAmountBeforeFreeShipping(
                    brandFreeShippingThreshold,
                    total,
                  ),
                })}
                .
              </plume.styles.copy.S>
            </SidebarItem>
          )}
          <SidebarItem>
            <plume.styles.copy.M>Échanges et retours</plume.styles.copy.M>
            <plume.styles.copy.S>
              {!product.isReturnable ? "Cet article n'est pas retournable." : product.vendor.returnInstructions ?? ''}
            </plume.styles.copy.S>
            <br />
            <plume.Link
              underlined
              as={'a'}
              href={
                url.aboutPage({
                  slug: 'livraison-retours',
                  title: '',
                }).path
              }
            >
              <plume.styles.copy.XS>Lire notre politique de livraison et retours</plume.styles.copy.XS>
            </plume.Link>
          </SidebarItem>
        </Sidebar>
      </Container>
    </>
  )
}

const BrandText = styled(plume.styles.copy.S)`
  color: ${plume.COLORS.GREY_SHADE_1};
`

const Container = styled.div`
  margin-top: 15px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr;
    gap: 0 32px;
    grid-template-areas: 'content side';
    margin-top: 12px;
  }
`

const ShippingDetails = styled.section`
  grid-area: content;
  margin-bottom: 40px;

  ${plume.Select} {
    margin-top: 15px;
    margin-bottom: 20px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 0;

    ${plume.Select} {
      margin-bottom: 30px;
      margin-top: 20px;
    }
  }
`

const Sidebar = styled.aside`
  grid-area: side;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    border-left: 1px solid ${plume.COLORS.GREY_SHADE_2};
    padding-left: 32px;
  }
`

const SidebarItem = styled.div`
  margin-bottom: 20px;

  ${plume.styles.copy.S} {
    color: ${plume.COLORS.GREY_SHADE_1};
  }

  ${plume.styles.copy.M} {
    font-weight: 600;
    margin-bottom: 8px;
  }

  ${plume.Link} {
    text-decoration: underline;

    ${plume.styles.copy.XS} {
      font-weight: 600;
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin-bottom: 32px;

    &:last-of-type {
      margin-bottom: 0px;
    }
  }
`

const ShippingList = styled.ul`
  margin-bottom: 15px;
`

const ShippingRate = styled.li`
  display: flex;
  justify-content: space-between;

  ${plume.styles.copy.M} {
    font-weight: 600;
  }

  ${plume.styles.copy.L} {
    font-weight: 600;
  }

  ${plume.styles.copy.S} {
    color: ${plume.COLORS.GREY_SHADE_2};
  }

  &:not(last-child) {
    margin-bottom: 14px;
  }
`
